import { useEffect, useState } from "react";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

import axios from "axios.js";
import WhatsAppTemplateComposer from "app/wacc/components/template_message_composer";

function NewConversationDialog({
  open,
  onClose,
  contact,
  reference,
  onSuccess,
}) {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  useEffect(() => {
    if (!open || !reference) return;

    let active = true;

    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      setTemplates([]);
      setStatus({ type: "", message: "" });

      try {
        const response = await axios.get(
          `/agents/wacc/numbers/${reference}/templates.json`,
        );

        if (!active) return;

        const result = response?.data;

        if (result?.status !== "success") {
          setStatus({
            type: "error",
            message:
              result?.data?.message ||
              result?.message ||
              "Failed to load message templates.",
          });
          return;
        }

        setTemplates(result?.data?.templates || []);
      } catch (error) {
        if (!active) return;

        setStatus({
          type: "error",
          message:
            error?.response?.data?.data?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "An error occurred while loading message templates.",
        });
      } finally {
        if (active) {
          setLoadingTemplates(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      active = false;
    };
  }, [open, reference]);

  const handleClose = () => {
    if (sending) return;

    setTemplateDialogOpen(false);
    setStatus({ type: "", message: "" });

    if (onClose) {
      onClose();
    }
  };

  const handleSendTemplate = async (templateMessage) => {
    setSending(true);
    setStatus({ type: "", message: "" });

    try {
      const payload = {
        number: reference,
        name: contact?.name || "",
        country_code: contact?.country_code || "234",
        phone_number: contact?.phone_number || "",
        template: templateMessage.template,
        parameters: templateMessage.components || [],
      };

      const response = await axios.post(
        "/agents/wacc/numbers/conversations/create.json",
        payload,
      );

      const result = response?.data;

      if (result?.status !== "success") {
        setStatus({
          type: "error",
          message:
            result?.data?.message ||
            result?.message ||
            "Failed to create the conversation.",
        });

        return;
      }

      setStatus({
        type: "success",
        message:
          result?.data?.message ||
          result?.message ||
          "Conversation created successfully.",
      });

      setTemplateDialogOpen(false);

      if (onSuccess) {
        onSuccess(result?.data);
      }
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error?.response?.data?.data?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "A network error occurred. Please try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 24px 80px rgba(0, 0, 0, 0.16)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            p: 0,
            background:
              "linear-gradient(135deg, #075E54 0%, #128C7E 65%, #25D366 100%)",
            color: "#fff",
          }}
        >
          <MDBox
            px={3}
            py={3}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <MDBox display="flex" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "15px",
                  backgroundColor: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "25px",
                }}
              >
                <Icon>forum</Icon>
              </MDBox>

              <MDBox>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "#fff", letterSpacing: "-0.4px" }}
                >
                  New Conversation
                </MDTypography>

                <MDTypography
                  variant="button"
                  sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 400 }}
                >
                  Start a WhatsApp conversation
                </MDTypography>
              </MDBox>
            </MDBox>

            <IconButton
              onClick={handleClose}
              disabled={sending}
              size="small"
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.14)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.25)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            backgroundColor: "#f8fafb",
            "&:first-of-type": {
              pt: 3,
            },
          }}
        >
          <MDBox display="flex" flexDirection="column" gap={2.5}>
            {/* Status */}
            {status.message && (
              <Alert
                severity={status.type === "success" ? "success" : "error"}
                onClose={() => setStatus({ type: "", message: "" })}
                sx={{
                  borderRadius: "12px",
                  alignItems: "center",
                  "& .MuiAlert-message": {
                    fontSize: "0.875rem",
                  },
                }}
              >
                {status.message}
              </Alert>
            )}

            {/* Contact Card */}
            <MDBox
              sx={{
                p: 2.5,
                backgroundColor: "#fff",
                borderRadius: "16px",
                border: "1px solid #e8edf0",
                boxShadow: "0 3px 12px rgba(0,0,0,0.025)",
              }}
            >
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  sx={{
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    fontSize: "0.68rem",
                  }}
                >
                  Recipient
                </MDTypography>

                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  sx={{
                    backgroundColor: "#e8f8ef",
                    px: 1.25,
                    py: 0.5,
                    borderRadius: "20px",
                  }}
                >
                  <MDBox
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: "#16a34a",
                    }}
                  />

                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    sx={{ color: "#15803d" }}
                  >
                    WhatsApp
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox display="flex" alignItems="center" gap={2}>
                <MDBox
                  sx={{
                    width: 54,
                    height: 54,
                    flexShrink: 0,
                    borderRadius: "17px",
                    background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#065f46",
                    fontSize: "23px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  {(contact?.name || "U").trim().charAt(0)}
                </MDBox>

                <MDBox flex={1} minWidth={0}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      color: "#1e293b",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {contact?.name || "Unnamed contact"}
                  </MDTypography>

                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "#64748b",
                      mt: 0.25,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {contact?.phone_number || "No phone number provided"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>

            {/* Template Section */}
            <MDBox>
              <MDBox mb={1.5}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: "#1e293b" }}
                >
                  Choose a message template
                </MDTypography>

                <MDTypography
                  variant="body2"
                  sx={{ color: "#64748b", mt: 0.5 }}
                >
                  Select an approved template to start the conversation.
                </MDTypography>
              </MDBox>

              {loadingTemplates ? (
                <MDBox
                  sx={{
                    py: 4,
                    px: 2,
                    backgroundColor: "#fff",
                    border: "1px solid #e8edf0",
                    borderRadius: "16px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <CircularProgress
                    size={30}
                    thickness={4}
                    sx={{ color: "#128C7E" }}
                  />

                  <MDTypography variant="button" sx={{ color: "#64748b" }}>
                    Loading approved templates...
                  </MDTypography>
                </MDBox>
              ) : templates.length > 0 ? (
                <MDBox
                  sx={{
                    p: 2,
                    backgroundColor: "#fff",
                    border: "1px solid #e8edf0",
                    borderRadius: "16px",
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1.5} mb={2}>
                    <MDBox
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "12px",
                        backgroundColor: "#e6f7f1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#128C7E",
                        fontSize: "20px",
                      }}
                    >
                      <i className="ni ni-email-83" />
                    </MDBox>

                    <MDBox flex={1}>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{ color: "#1e293b" }}
                      >
                        Approved templates
                      </MDTypography>

                      <MDTypography
                        variant="caption"
                        display="block"
                        sx={{ color: "#64748b" }}
                      >
                        {templates.length}{" "}
                        {templates.length === 1 ? "template" : "templates"}{" "}
                        available
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDButton
                    variant="contained"
                    color="success"
                    onClick={() => {
                      setStatus({ type: "", message: "" });
                      setTemplateDialogOpen(true);
                    }}
                    disabled={
                      sending ||
                      !contact?.phone_number ||
                      !reference ||
                      templates.length === 0
                    }
                    fullWidth
                    sx={{
                      py: 1.5,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontSize: "0.9rem",
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #128C7E, #075E54)",
                      boxShadow: "0 5px 14px rgba(18,140,126,0.2)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #0f766e, #064e45)",
                        boxShadow: "0 7px 18px rgba(18,140,126,0.28)",
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        background: "#cbd5e1",
                        color: "#fff",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    Choose Template
                    <i
                      className="ni ni-bold-right"
                      style={{ marginLeft: 10, fontSize: 12 }}
                    />
                  </MDButton>
                </MDBox>
              ) : (
                <MDBox
                  sx={{
                    p: 3,
                    backgroundColor: "#fff",
                    border: "1px dashed #cbd5e1",
                    borderRadius: "16px",
                    textAlign: "center",
                  }}
                >
                  <MDBox
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "16px",
                      backgroundColor: "#fff7ed",
                      color: "#ea580c",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "23px",
                      mx: "auto",
                      mb: 1.5,
                    }}
                  >
                    <i className="ni ni-notification-70" />
                  </MDBox>

                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{ color: "#334155" }}
                  >
                    No templates available
                  </MDTypography>

                  <MDTypography
                    variant="body2"
                    sx={{
                      color: "#64748b",
                      mt: 0.75,
                      maxWidth: 300,
                      mx: "auto",
                    }}
                  >
                    {status.type === "error"
                      ? "We couldn't load your templates. Please try again."
                      : "There are no approved message templates available for this business number."}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            {/* Sending State */}
            {sending && (
              <MDBox
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  backgroundColor: "#e6f7f1",
                  borderRadius: "12px",
                  border: "1px solid #b7e4d7",
                }}
              >
                <CircularProgress
                  size={20}
                  thickness={4}
                  sx={{ color: "#128C7E" }}
                />

                <MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{ color: "#075E54" }}
                  >
                    Creating conversation...
                  </MDTypography>

                  <MDTypography
                    variant="caption"
                    display="block"
                    sx={{ color: "#47796e" }}
                  >
                    Please wait while we process your request.
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
      </Dialog>

      <WhatsAppTemplateComposer
        open={templateDialogOpen}
        onCancel={() => {
          if (!sending) {
            setTemplateDialogOpen(false);
          }
        }}
        contact={contact}
        templates={templates}
        sendTemplate={handleSendTemplate}
      />
    </>
  );
}

export default NewConversationDialog;
